import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Target, ShoppingBag, Calendar, Radio, Users, DollarSign, ExternalLink, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { marketingAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';

export default function SmartLinkPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    marketingAPI.getSmartLink(slug)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-950"><div className="w-8 h-8 border-2 border-[#b8ff00] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <div className="text-center">
        <p className="text-zinc-500 text-lg mb-4">Creator not found</p>
        <Link to="/"><Button className="bg-[#b8ff00] text-black font-bold">Go Home</Button></Link>
      </div>
    </div>
  );

  const s = data.stats || {};

  return (
    <div className="min-h-screen bg-zinc-950 pb-20" data-testid="smart-link-page">
      {/* Hero */}
      <div className="bg-gradient-to-b from-zinc-900 to-zinc-950 border-b border-zinc-800">
        <div className="max-w-lg mx-auto px-4 py-10 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-[#b8ff00] to-[#00ff88] rounded-2xl flex items-center justify-center">
            <span className="font-display font-black text-3xl text-black">{data.name?.[0] || '?'}</span>
          </motion.div>
          <h1 className="font-display font-bold text-2xl text-white mb-1">{data.name}</h1>
          {data.genre && <span className="inline-block px-3 py-1 bg-zinc-800 text-zinc-300 text-xs font-bold rounded-full mb-3">{data.genre}</span>}
          {data.bio && <p className="text-sm text-zinc-400 max-w-sm mx-auto leading-relaxed">{data.bio}</p>}

          {/* Stats row */}
          <div className="flex justify-center gap-6 mt-5">
            <div className="text-center">
              <p className="text-lg font-bold text-white">{s.followers}</p>
              <p className="text-[10px] text-zinc-500 uppercase">Followers</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-[#b8ff00]">{formatCurrency(s.total_funded)}</p>
              <p className="text-[10px] text-zinc-500 uppercase">Funded</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-white">{s.backers}</p>
              <p className="text-[10px] text-zinc-500 uppercase">Backers</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 mt-6 space-y-4">
        {/* Live rooms */}
        {data.rooms?.filter(r => r.is_live).length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            {data.rooms.filter(r => r.is_live).map(room => (
              <Link key={room.id} to={`/creator/${room.id}`} className="block p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl mb-2 group hover:border-rose-500/40 transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-rose-500/20 rounded-xl flex items-center justify-center">
                    <Radio size={18} className="text-rose-400 animate-pulse" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-rose-300">LIVE NOW: {room.name}</p>
                    <p className="text-[10px] text-rose-400/60">Tap to join and tip</p>
                  </div>
                  <ArrowRight size={16} className="text-rose-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </motion.div>
        )}

        {/* Projects */}
        {data.projects?.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Projects</p>
            <div className="space-y-2">
              {data.projects.map((p, i) => {
                const pct = Math.round((p.current_funding / Math.max(p.funding_goal, 1)) * 100);
                return (
                  <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                    <Link to={`/projects/${p.id}`} className="block p-4 bg-zinc-900 border border-zinc-800 rounded-2xl hover:border-zinc-700 transition-all group">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Target size={14} className="text-[#b8ff00]" />
                          <span className="font-bold text-sm text-white">{p.title}</span>
                        </div>
                        <span className={`text-xs font-bold ${p.status === 'funded' ? 'text-emerald-400' : 'text-[#b8ff00]'}`}>
                          {pct}% funded
                        </span>
                      </div>
                      <div className="w-full bg-zinc-800 h-1.5 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#b8ff00] to-[#00ff88] rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                      </div>
                      <p className="text-[10px] text-zinc-500 mt-1.5">{p.genre} · {formatCurrency(p.current_funding)} of {formatCurrency(p.funding_goal)}</p>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Products */}
        {data.products?.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Shop</p>
            <div className="grid grid-cols-2 gap-2">
              {data.products.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Link to={`/product/${p.id}`} className="block p-3 bg-zinc-900 border border-zinc-800 rounded-xl hover:border-zinc-700 transition-all">
                    {p.image_url && <div className="w-full h-24 bg-zinc-800 rounded-lg mb-2 overflow-hidden"><img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /></div>}
                    <p className="text-xs font-bold text-white truncate">{p.name}</p>
                    <p className="text-sm font-bold text-[#b8ff00]">{formatCurrency(p.price)}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Events */}
        {data.events?.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Events</p>
            <div className="space-y-2">
              {data.events.map((e, i) => (
                <motion.div key={e.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Link to={`/events/${e.id}`} className="flex items-center gap-3 p-3 bg-zinc-900 border border-zinc-800 rounded-xl hover:border-zinc-700 transition-all">
                    <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar size={16} className="text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-white truncate">{e.title}</p>
                      <p className="text-[10px] text-zinc-500">{e.venue || e.location} · {e.event_date?.split('T')[0]}</p>
                    </div>
                    <ArrowRight size={14} className="text-zinc-600" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Rooms (not live) */}
        {data.rooms?.filter(r => !r.is_live).length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Creator Rooms</p>
            {data.rooms.filter(r => !r.is_live).map(room => (
              <Link key={room.id} to={`/creator/${room.id}`} className="flex items-center gap-3 p-3 bg-zinc-900 border border-zinc-800 rounded-xl hover:border-zinc-700 transition-all mb-2">
                <Radio size={14} className="text-zinc-500" />
                <span className="text-xs font-medium text-zinc-300">{room.name}</span>
              </Link>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-6">
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors">
            <div className="w-4 h-4 bg-gradient-to-br from-[#b8ff00] to-[#00ff88] rounded flex items-center justify-center">
              <span className="text-[8px] font-bold text-black">M</span>
            </div>
            Powered by MMarketing OS
          </Link>
        </div>
      </div>
    </div>
  );
}
